import React, { useEffect, useState } from 'react';
import { X, Trash2, Loader2, Film, Image as ImageIcon, Clock } from 'lucide-react';
import { apiFetch } from '../App';
import { toast } from 'sonner';

/**
 * Slide-in drawer that lists the current user's slideshow drafts
 * and lets them load or delete one.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onLoadDraft: (draft: FullDraft) => void  // called with the full draft object
 */
export default function DraftsDrawer({ open, onClose, onLoadDraft }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await apiFetch('/slideshow-drafts');
        setDrafts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load drafts:', err);
        toast.error('Failed to load drafts');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const handleLoad = async (draftId) => {
    setLoadingId(draftId);
    try {
      const { data } = await apiFetch(`/slideshow-drafts/${draftId}`);
      onLoadDraft?.(data);
      onClose?.();
      toast.success(`Loaded "${data.name}"`);
    } catch (err) {
      console.error('Load draft failed:', err);
      toast.error('Could not load draft');
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (draftId, name) => {
    if (!window.confirm(`Delete draft "${name}"? This cannot be undone.`)) return;
    setDeletingId(draftId);
    try {
      await apiFetch(`/slideshow-drafts/${draftId}`, { method: 'DELETE' });
      setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
      toast.success('Draft deleted');
    } catch (err) {
      console.error('Delete draft failed:', err);
      toast.error('Could not delete draft');
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="drafts-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative h-full w-full max-w-md bg-zinc-950 border-l border-white/10 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h3 className="text-white text-lg font-semibold">Slideshow Drafts</h3>
            <p className="text-zinc-500 text-xs mt-0.5">Up to 20 saved drafts per account</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
            data-testid="drafts-drawer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#915ee9]" />
            </div>
          ) : drafts.length === 0 ? (
            <div className="text-center py-16 px-4">
              <Film className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No drafts yet</p>
              <p className="text-zinc-600 text-xs mt-2">
                Save your editor state here to pick up right where you left off later.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {drafts.map(d => (
                <div
                  key={d.draft_id}
                  className="group flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#915ee9]/40 hover:bg-white/[0.04] transition-colors"
                  data-testid={`draft-card-${d.draft_id}`}
                >
                  {/* Thumbnail */}
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-zinc-900 border border-white/10 flex-shrink-0 flex items-center justify-center">
                    {d.thumbnail_url ? (
                      <img src={d.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="h-5 w-5 text-zinc-600" />
                    )}
                  </div>
                  {/* Info + Load */}
                  <button
                    type="button"
                    onClick={() => handleLoad(d.draft_id)}
                    disabled={loadingId === d.draft_id}
                    className="flex-1 min-w-0 text-left disabled:opacity-60"
                    data-testid={`draft-load-${d.draft_id}`}
                  >
                    <div className="text-white text-sm font-medium truncate">{d.name}</div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                      <span>{d.photo_count} item{d.photo_count === 1 ? '' : 's'}</span>
                      {d.video_count > 0 && <span>• {d.video_count} video</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(d.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </button>
                  {loadingId === d.draft_id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-[#915ee9]" />
                  ) : (
                    <button
                      onClick={() => handleDelete(d.draft_id, d.name)}
                      disabled={deletingId === d.draft_id}
                      className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-60"
                      data-testid={`draft-delete-${d.draft_id}`}
                    >
                      {deletingId === d.draft_id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Trash2 className="h-4 w-4" />
                      }
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
